import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { ChevronRight } from 'lucide-react';
import { createContext, PropsWithChildren, useContext } from 'react';
import { useHubChromeEnabled } from 'utils/featureFlags';

type MapSidebarContainerProps = {
  isMainSidebarVisible: boolean;
  setIsMainSidebarVisible: (visible: boolean) => void;
  /** Map hidden: the panel is the whole workspace, not the left half. */
  fullWidth?: boolean;
};

type MapSidebarCollapseContextValue = {
  isMainSidebarVisible: boolean;
  setIsMainSidebarVisible: (visible: boolean) => void;
};

const MapSidebarCollapseContext =
  createContext<MapSidebarCollapseContextValue | null>(null);

export const useMapSidebarCollapse = () => {
  return useContext(MapSidebarCollapseContext);
};

export const MapSidebarContainer = ({
  children,
  isMainSidebarVisible,
  setIsMainSidebarVisible,
  fullWidth = false,
}: PropsWithChildren<MapSidebarContainerProps>) => {
  const hubChrome = useHubChromeEnabled();
  const panelWidth = fullWidth ? '100%' : '50%';

  const collapseValue: MapSidebarCollapseContextValue = {
    isMainSidebarVisible,
    setIsMainSidebarVisible,
  };

  if (hubChrome) {
    return (
      <MapSidebarCollapseContext.Provider value={collapseValue}>
        <Collapsible
          open={isMainSidebarVisible}
          onOpenChange={setIsMainSidebarVisible}
          className="dark tw:contents tw:text-foreground"
        >
          {isMainSidebarVisible ? (
            <div
              className="d-flex align-items-stretch h-100"
              style={{ width: panelWidth, minHeight: 0 }}
            >
              <div
                className={cn(
                  'map-left-sidebar',
                  'tw:bg-background tw:text-foreground'
                )}
                style={{ backgroundColor: 'var(--background, #0a0a0a)' }}
              >
                {children}
              </div>
            </div>
          ) : (
            <div
              className="dark tw:flex tw:h-full tw:w-14 tw:shrink-0 tw:flex-col tw:items-center tw:border-r tw:border-border tw:bg-background tw:p-2"
              style={{ backgroundColor: 'var(--background, #0a0a0a)' }}
            >
              <CollapsibleTrigger
                render={
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    aria-label="Expand panel"
                  />
                }
              >
                <ChevronRight className="tw:h-4 tw:w-4" />
              </CollapsibleTrigger>
            </div>
          )}
        </Collapsible>
      </MapSidebarCollapseContext.Provider>
    );
  }

  return (
    <MapSidebarCollapseContext.Provider value={collapseValue}>
      {isMainSidebarVisible ? (
        <div
          className="d-flex align-items-stretch h-100"
          style={{ width: panelWidth, minHeight: 0 }}
        >
          <div className="map-left-sidebar">{children}</div>
          {setIsMainSidebarVisible && (
            <StylishNewButton
              className="button--secondary map-sidebar-button"
              style={{ backgroundColor: 'var(--background-dark)' }}
              onClick={() => {
                setIsMainSidebarVisible(false);
              }}
            >
              <SharedIcon iconName="chevron_left" size="sm" />
            </StylishNewButton>
          )}
        </div>
      ) : (
        <StylishNewButton
          className="button--secondary map-sidebar-button map-sidebar-button-in"
          style={{ backgroundColor: 'var(--background-dark)' }}
          onClick={() => {
            setIsMainSidebarVisible(true);
          }}
        >
          <SharedIcon iconName="chevron_right" size="sm" />
        </StylishNewButton>
      )}
    </MapSidebarCollapseContext.Provider>
  );
};
